<template>
  <!--
    DeckPickerModal — single modal combining info + 78-card grid.

    Iteration 3 (2026-05-05):
      * Larger card thumbnails (~140px wide vs prior ~96)
      * Top-right gap filled with stats panel + intent line
      * Suit/arcana filter chips above the grid
      * Click a card → fullscreen lightbox (close on click anywhere)
  -->
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal-card">
      <header class="modal-header">
        <div>
          <h2 class="modal-title">{{ deck?.name || 'Deck' }}</h2>
          <p class="modal-sub" v-if="brandSub">{{ brandSub }}</p>
        </div>
        <button class="close-btn" @click="$emit('close')">×</button>
      </header>

      <div class="modal-body">
        <section class="hero-row">
          <div class="hero-frame">
            <img
              v-if="backUrl"
              :src="backUrl"
              :alt="(deck?.name || 'deck') + ' card back'"
              class="hero-img"
            />
            <div v-else class="hero-placeholder">
              <span class="placeholder-icon">✦</span>
              <span>No back generated yet</span>
            </div>
          </div>
          <div class="info-col">
            <dl class="meta-grid">
              <template v-if="deck?.theme"><dt>Theme</dt><dd>{{ deck.theme }}</dd></template>
              <template v-if="deck?.style"><dt>Style</dt><dd>{{ deck.style }}</dd></template>
              <template v-if="deck?.mood"><dt>Mood</dt><dd>{{ deck.mood }}</dd></template>
              <template v-if="deck?.color_palette"><dt>Palette</dt><dd>{{ deck.color_palette }}</dd></template>
              <template v-if="deck?.motif"><dt>Motif</dt><dd>{{ deck.motif }}</dd></template>
              <template v-if="deck?.era"><dt>Era</dt><dd>{{ deck.era }}</dd></template>
              <template v-if="deck?.energy"><dt>Energy</dt><dd>{{ deck.energy }}</dd></template>
            </dl>
            <p v-if="intentLine" class="intent">{{ intentLine }}</p>
            <div class="stats-row">
              <div class="stat">
                <div class="stat-num">{{ cards.length || 78 }}</div>
                <div class="stat-label">Cards</div>
              </div>
              <div class="stat">
                <div class="stat-num pass">{{ auditStats.passed }}</div>
                <div class="stat-label">Audit Passed</div>
              </div>
              <div v-if="auditStats.failed" class="stat">
                <div class="stat-num fail">{{ auditStats.failed }}</div>
                <div class="stat-label">Failed</div>
              </div>
              <div v-if="auditStats.pending" class="stat">
                <div class="stat-num pending">{{ auditStats.pending }}</div>
                <div class="stat-label">Pending</div>
              </div>
            </div>
          </div>
        </section>

        <section class="cards-section">
          <div class="section-bar">
            <h3 class="section-title">
              {{ loadingCards
                  ? 'Loading cards…'
                  : (filteredCards.length === cards.length
                      ? `${cards.length} cards in this deck`
                      : `${filteredCards.length} of ${cards.length} cards`) }}
            </h3>
            <div v-if="!loadingCards && cards.length" class="filters" role="tablist">
              <button
                v-for="f in suitFilters"
                :key="f.key"
                type="button"
                class="filter-chip"
                :class="{ active: activeSuit === f.key }"
                :aria-selected="activeSuit === f.key"
                @click="activeSuit = f.key"
              >
                <span class="filter-icon">{{ f.icon }}</span>
                <span>{{ f.label }}</span>
                <span class="filter-count">{{ countBySuit[f.key] || 0 }}</span>
              </button>
            </div>
          </div>
          <div v-if="loadingCards" class="cards-loading">Loading…</div>
          <div v-else-if="!cards.length" class="cards-loading">
            No cards generated yet for this deck.
          </div>
          <div v-else class="cards-grid">
            <button
              v-for="c in filteredCards"
              :key="c.id"
              type="button"
              class="card-tile"
              @click="lightboxCard = c"
              :aria-label="'View ' + c.card__name + ' full size'"
            >
              <img :src="c.preview_url || c.image_url" :alt="c.card__name" />
              <div class="card-name">{{ c.card__name }}</div>
            </button>
          </div>
        </section>

        <p v-if="confirmError" class="error">⚠ {{ confirmError }}</p>
      </div>

      <footer class="modal-footer">
        <button class="ghost-btn" @click="$emit('close')" :disabled="confirming || togglingFav">
          Cancel
        </button>
        <button
          class="ghost-btn fav-btn"
          :disabled="confirming || togglingFav"
          @click="toggleFavorite"
        >
          {{ togglingFav ? 'Saving…' : (isFavorited ? 'Remove from Favorites' : 'Add to Favorites') }}
        </button>
        <button
          class="primary-btn"
          :disabled="isCurrent || confirming"
          @click="confirmSelect"
        >
          {{ confirming ? 'Saving…' : (isCurrent ? '✓ Already your deck' : 'Set as Default') }}
        </button>
      </footer>
    </div>

    <!-- ── Card lightbox ───────────────────────────────────────── -->
    <!-- Click a thumbnail → full-screen image. Click anywhere on the -->
    <!-- backdrop or press × to close. -->
    <div
      v-if="lightboxCard"
      class="lightbox"
      @click.self="lightboxCard = null"
    >
      <button class="lightbox-close" @click="lightboxCard = null" aria-label="Close">×</button>
      <div class="lightbox-content" @click.self="lightboxCard = null">
        <img
          :src="lightboxCard.image_url || lightboxCard.preview_url"
          :alt="lightboxCard.card__name"
          class="lightbox-img"
        />
        <div class="lightbox-name">{{ lightboxCard.card__name }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { store } from '@/store'
import * as api from '@/api-service.js'

const props = defineProps({
  deck: { type: Object, required: true },
  variant: { type: Object, required: true },
})
const emit = defineEmits(['close', 'selected'])

const cards = ref([])
const loadingCards = ref(true)
const confirming = ref(false)
const confirmError = ref('')

// Suit/arcana filter — backend returns ``card__suit`` codes:
//   MJ (Major Arcana), CP (Cups), WD (Wands), SW (Swords), PT (Pentacles)
const SUIT_FILTERS = [
  { key: 'all',    label: 'All',     icon: '✦' },
  { key: 'MJ',     label: 'Major',   icon: '★' },
  { key: 'CP',     label: 'Cups',    icon: '♥' },
  { key: 'WD',     label: 'Wands',   icon: '⚡' },
  { key: 'SW',     label: 'Swords',  icon: '⚔' },
  { key: 'PT',     label: 'Pentacles', icon: '◈' },
]
const activeSuit = ref('all')

// Click-to-zoom lightbox for a single card
const lightboxCard = ref(null)

const backUrl = computed(() => (
  props.deck?.back_image_url
    || props.deck?.default_card_back?.image_url
    || props.deck?.default_card_back?.preview_url
    || ''
))
const isCurrent = computed(() => (
  props.deck?.slug === props.variant?.deck_template_slug
))
const brandSub = computed(() => {
  const parts = [props.deck?.theme, props.deck?.style, props.deck?.mood]
    .filter(Boolean)
  return parts.join(' · ')
})

// "Intent" line synthesizes the deck's brand fields into a sentence.
// Fills the previously-empty top-right space with something readable
// instead of more dt/dd grid rows.
const intentLine = computed(() => {
  const d = props.deck || {}
  if (!d.theme) return ''
  const bits = [`A ${d.theme.toLowerCase()} deck`]
  if (d.style) bits.push(`in ${d.style.toLowerCase()} style`)
  if (d.mood) bits.push(`with a ${d.mood.toLowerCase()} mood`)
  if (d.motif) bits.push(`built around ${d.motif.toLowerCase()}`)
  return bits.join(', ') + '.'
})

const suitFilters = computed(() => SUIT_FILTERS)
const countBySuit = computed(() => {
  const counts = { all: cards.value.length }
  for (const c of cards.value) {
    const s = c.card__suit || 'MJ'
    counts[s] = (counts[s] || 0) + 1
  }
  return counts
})
const filteredCards = computed(() => {
  if (activeSuit.value === 'all') return cards.value
  return cards.value.filter(c => (c.card__suit || 'MJ') === activeSuit.value)
})

const auditStats = computed(() => {
  const r = { passed: 0, failed: 0, pending: 0, manual: 0 }
  for (const c of cards.value) {
    if (c.audit_status === 'passed') r.passed++
    else if (c.audit_status === 'failed') r.failed++
    else if (c.audit_status === 'manual_override') r.manual++
    else r.pending++
  }
  return r
})

// ── Favorites state ─────────────────────────────────────────────
const isFavorited = ref(false)
const togglingFav = ref(false)

onMounted(async () => {
  // Load card preview + favorite status in parallel.
  const tasks = []
  if (props.deck?.slug) {
    tasks.push(
      api.previewDeckTemplate(props.deck.slug).then((data) => {
        cards.value = data?.cards || []
      }),
    )
    tasks.push(
      api.listCustomDeckFavorites().then((favs) => {
        isFavorited.value = (favs || []).some(
          f => f.slug === props.deck.slug,
        )
      }),
    )
  }
  try {
    await Promise.all(tasks)
  } finally {
    loadingCards.value = false
  }
})

async function toggleFavorite() {
  if (!props.deck?.slug) return
  togglingFav.value = true
  try {
    if (isFavorited.value) {
      await api.removeCustomDeckFavorite(props.deck.slug)
      isFavorited.value = false
    } else {
      await api.addCustomDeckFavorite(props.deck.slug)
      isFavorited.value = true
    }
    // Per direction, modal closes after the favorite action so the
    // operator returns to the gallery and can see the change.
    emit('close')
  } catch (e) {
    confirmError.value = e?.response?.data?.error || String(e)
  } finally {
    togglingFav.value = false
  }
}

async function confirmSelect() {
  if (isCurrent.value) return
  confirming.value = true
  confirmError.value = ''
  try {
    await api.chooseDeckTemplate(props.deck.slug, {
      skill_key: 'tarot',
      variant_key: props.variant.variant_key || 'one_card',
    })
    const fresh = await api.listSkillVariants()
    store.readerSkillVariants = fresh
    emit('selected', props.deck.slug)
  } catch (e) {
    confirmError.value = e?.response?.data?.error || String(e)
  } finally {
    confirming.value = false
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}
.modal-card {
  background: #1a0c2e;
  border: 1px solid rgba(195,169,255,0.18);
  border-radius: 14px;
  width: 100%;
  max-width: 1080px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.modal-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 22px;
  margin: 0 0 4px;
  text-transform: capitalize;
}
.modal-sub {
  color: rgba(212, 196, 232, 0.7);
  font-size: 12px;
  margin: 0;
  text-transform: capitalize;
}
.close-btn {
  background: transparent;
  color: rgba(212,196,232,0.7);
  border: none;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px;
}
.close-btn:hover { color: #fff; }

.modal-body {
  padding: 20px 24px 8px;
  overflow-y: auto;
  flex: 1;
}

/* ── Hero row: back image + info column ────────────────────── */
.hero-row {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 28px;
  align-items: start;
  margin-bottom: 24px;
}
.hero-frame {
  aspect-ratio: 2 / 3;
  background: rgba(0,0,0,0.45);
  border-radius: 12px;
  border: 1px solid rgba(195,169,255,0.18);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 28px rgba(0,0,0,0.45);
}
.hero-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.hero-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(212,196,232,0.5);
  font-size: 11px;
  font-style: italic;
  padding: 12px;
  text-align: center;
}
.placeholder-icon { font-size: 28px; color: rgba(195,169,255,0.35); }

.info-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.meta-grid {
  display: grid;
  grid-template-columns: 90px 1fr;
  row-gap: 6px;
  column-gap: 14px;
  margin: 0;
  font-size: 13px;
  color: rgba(212, 196, 232, 0.9);
  align-content: start;
}
.meta-grid dt {
  color: rgba(212, 196, 232, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 10px;
  padding-top: 3px;
}
.meta-grid dd {
  margin: 0;
  text-transform: capitalize;
}
.intent {
  font-style: italic;
  font-size: 13px;
  color: rgba(212, 196, 232, 0.78);
  line-height: 1.5;
  margin: 0;
  padding: 10px 14px;
  border-left: 2px solid rgba(201,169,78,0.45);
  background: rgba(201,169,78,0.04);
  border-radius: 0 6px 6px 0;
}
.stats-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.stat {
  flex: 1 1 80px;
  min-width: 70px;
  text-align: center;
  padding: 10px 8px;
  background: rgba(0,0,0,0.32);
  border: 1px solid rgba(195,169,255,0.16);
  border-radius: 8px;
}
.stat-num {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  color: #C9A94E;
  line-height: 1;
}
.stat-num.pass { color: #69d68b; }
.stat-num.fail { color: #f28b8b; }
.stat-num.pending { color: rgba(212,196,232,0.7); }
.stat-label {
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(212,196,232,0.6);
  margin-top: 4px;
}

/* ── Cards section ───────────────────────────────────────────── */
.cards-section { margin-top: 4px; }
.section-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 0;
  margin-bottom: 14px;
  border-top: 1px solid rgba(195,169,255,0.12);
  border-bottom: 1px solid rgba(195,169,255,0.12);
}
.section-title {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(201,169,78,0.85);
  margin: 0;
  flex-shrink: 0;
}
.filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 16px;
  border: 1px solid rgba(195,169,255,0.22);
  background: rgba(0,0,0,0.32);
  color: rgba(212,196,232,0.75);
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.filter-chip:hover {
  border-color: rgba(195,169,255,0.45);
  color: rgba(212,196,232,0.95);
}
.filter-chip.active {
  background: rgba(102,51,153,0.32);
  border-color: rgba(201,169,78,0.6);
  color: #fff;
}
.filter-icon { font-size: 13px; line-height: 1; }
.filter-count {
  background: rgba(0,0,0,0.45);
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 9px;
  letter-spacing: 0.04em;
  color: rgba(212,196,232,0.7);
}
.filter-chip.active .filter-count {
  color: #C9A94E;
  background: rgba(0,0,0,0.55);
}

.cards-loading {
  text-align: center;
  padding: 36px;
  color: rgba(212, 196, 232, 0.55);
  font-style: italic;
}
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 12px;
}
.card-tile {
  background: rgba(255,255,255,0.03);
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 6px;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  transition: all 0.15s;
}
.card-tile:hover {
  border-color: rgba(195,169,255,0.35);
  transform: translateY(-2px);
  background: rgba(255,255,255,0.05);
}
.card-tile img {
  width: 100%;
  aspect-ratio: 2 / 3;
  object-fit: cover;
  border-radius: 5px;
  display: block;
}
.card-name {
  font-size: 11px;
  color: rgba(212,196,232,0.85);
  text-align: center;
  margin-top: 5px;
  line-height: 1.2;
  min-height: 26px;
}

/* ── Card lightbox ────────────────────────────────────────────── */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 32px;
  cursor: zoom-out;
}
.lightbox-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  max-width: 100%;
  max-height: 100%;
}
.lightbox-img {
  max-width: min(560px, 92vw);
  max-height: 86vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 38px rgba(201,169,78,0.18);
}
.lightbox-name {
  font-family: 'Cinzel', serif;
  font-size: 18px;
  color: #C9A94E;
  text-align: center;
  text-transform: capitalize;
}
.lightbox-close {
  position: absolute;
  top: 18px;
  right: 22px;
  background: transparent;
  color: rgba(232,216,255,0.85);
  border: 1px solid rgba(195,169,255,0.25);
  border-radius: 50%;
  width: 38px;
  height: 38px;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.lightbox-close:hover {
  border-color: #C9A94E;
  color: #C9A94E;
}

.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  text-align: center;
  margin: 12px 0 0;
}

/* ── Footer ──────────────────────────────────────────────────── */
.modal-footer {
  padding: 14px 24px;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}
.ghost-btn, .primary-btn {
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
}
.ghost-btn {
  background: transparent;
  border: 1px solid rgba(195,169,255,0.32);
  color: rgba(212, 196, 232, 0.85);
}
.ghost-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.ghost-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.primary-btn {
  background: #663399;
  color: #fff;
  border: none;
}
.primary-btn:hover:not(:disabled) { background: #7a3fb1; }
.primary-btn:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 720px) {
  .hero-row {
    grid-template-columns: 1fr;
  }
  .hero-frame {
    max-width: 220px;
    margin: 0 auto;
  }
  .section-bar { flex-direction: column; align-items: stretch; }
  .filters { justify-content: center; }
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  }
  .lightbox-img {
    max-width: 92vw;
    max-height: 78vh;
  }
}
</style>
